import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { format } from 'date-fns';
import {
  Settings as SettingsIcon,
  Upload,
  Trash2,
  CheckCircle2,
  AlertCircle,
  Send,
  Loader2,
  FileJson,
  Key
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Settings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [fcm, setFcm] = useState(null);
  const [testToken, setTestToken] = useState('');
  const fileRef = useRef(null);

  const fetchFcm = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/settings/fcm`);
      setFcm(res.data);
    } catch (err) {
      toast.error('Failed to load FCM settings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchFcm(); }, []);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.endsWith('.json')) {
      toast.error('Please choose a .json file');
      e.target.value = '';
      return;
    }
    try {
      setSaving(true);
      const text = await file.text();
      let json;
      try { json = JSON.parse(text); }
      catch (_) { toast.error('File is not valid JSON'); return; }

      const res = await axios.put(`${API}/settings/fcm`, json);
      toast.success(res.data?.message || 'FCM credentials saved');
      setFcm(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save FCM credentials');
    } finally {
      setSaving(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const removeFcm = async () => {
    if (!window.confirm('Remove the saved FCM service account?')) return;
    try {
      setRemoving(true);
      await axios.delete(`${API}/settings/fcm`);
      toast.success('FCM credentials removed');
      setFcm({ configured: false, serviceAccount: null });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to remove');
    } finally {
      setRemoving(false);
    }
  };

  const sendTest = async () => {
    if (!testToken.trim()) { toast.error('Paste a device FCM token first'); return; }
    try {
      setTesting(true);
      const res = await axios.post(`${API}/settings/fcm/test`, { token: testToken.trim() });
      if (res.data?.ok) toast.success('Test push delivered to the device');
      else toast.error(`Test failed: ${(res.data?.errors || []).join(', ') || 'no success'}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Test failed');
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary" />
      </div>
    );
  }

  const sa = fcm?.serviceAccount;

  return (
    <div className="p-4 sm:p-6 lg:p-8" style={{ fontFamily: 'Public Sans, sans-serif' }}>
      <div className="mb-6">
        <h1 className="text-3xl font-bold flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif', color: '#0F172A' }}>
          <SettingsIcon size={26} className="text-orange-600" /> Settings
        </h1>
        <p className="text-gray-600 text-sm mt-1">
          Configure platform integrations like push notifications.
        </p>
      </div>

      {/* FCM SERVICE ACCOUNT */}
      <div className="bg-white border border-gray-200 rounded-xl p-6 mb-6" data-testid="fcm-settings-card">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h2 className="text-lg font-semibold flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              <Key size={18} className="text-orange-600" />
              Push Notifications (Firebase Cloud Messaging)
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              Upload your Firebase <strong>service account JSON</strong> to enable push delivery for
              every notification you send from this admin panel. The system can deliver to
              <strong> 1 lakh+ devices</strong> in a few minutes using a parallel send pool.
            </p>
          </div>
          {fcm?.configured ? (
            <Badge className="bg-emerald-100 text-emerald-800 border border-emerald-200 flex items-center gap-1" data-testid="fcm-status-configured">
              <CheckCircle2 size={12} /> Connected
            </Badge>
          ) : (
            <Badge className="bg-amber-100 text-amber-900 border border-amber-200 flex items-center gap-1" data-testid="fcm-status-missing">
              <AlertCircle size={12} /> Not configured
            </Badge>
          )}
        </div>

        {/* current credentials */}
        {sa && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4 text-xs">
            <div className="rounded border border-gray-200 bg-gray-50 p-2.5">
              <p className="text-[10px] uppercase tracking-wide font-semibold text-gray-500">Project ID</p>
              <p className="font-mono text-gray-800 break-all" data-testid="fcm-project-id">{sa.project_id}</p>
            </div>
            <div className="rounded border border-gray-200 bg-gray-50 p-2.5">
              <p className="text-[10px] uppercase tracking-wide font-semibold text-gray-500">Client Email</p>
              <p className="font-mono text-gray-800 break-all">{sa.client_email}</p>
            </div>
            <div className="rounded border border-gray-200 bg-gray-50 p-2.5">
              <p className="text-[10px] uppercase tracking-wide font-semibold text-gray-500">Private Key ID</p>
              <p className="font-mono text-gray-800 break-all">{sa.private_key_id}</p>
            </div>
            <div className="rounded border border-gray-200 bg-gray-50 p-2.5">
              <p className="text-[10px] uppercase tracking-wide font-semibold text-gray-500">Private Key</p>
              <p className="font-mono text-gray-800">{sa.private_key_preview || '—'}</p>
            </div>
            {fcm?.updatedAt && (
              <div className="rounded border border-gray-200 bg-gray-50 p-2.5 md:col-span-2">
                <p className="text-[10px] uppercase tracking-wide font-semibold text-gray-500">Last updated</p>
                <p className="text-gray-800">
                  {format(new Date(fcm.updatedAt), 'dd MMM yyyy, hh:mm a')}
                  {fcm.updatedByName ? ` · by ${fcm.updatedByName}` : ''}
                </p>
              </div>
            )}
          </div>
        )}

        {/* uploader */}
        <div className="border-2 border-dashed border-orange-200 bg-orange-50/60 rounded-lg p-4 flex items-center gap-3">
          <FileJson size={28} className="text-orange-600 flex-shrink-0" />
          <div className="flex-1">
            <p className="text-sm font-medium text-gray-900">
              {fcm?.configured ? 'Replace service account JSON' : 'Upload service account JSON'}
            </p>
            <p className="text-xs text-gray-600">
              Generate it in Firebase Console → Project settings → Service accounts → Generate new private key.
            </p>
          </div>
          <input
            ref={fileRef}
            type="file"
            accept="application/json,.json"
            onChange={handleFile}
            className="hidden"
            data-testid="fcm-file-input"
          />
          <Button
            onClick={() => fileRef.current?.click()}
            disabled={saving}
            data-testid="fcm-upload-button"
            style={{ backgroundColor: '#C2410C' }}
            className="text-white"
          >
            {saving ? <Loader2 className="animate-spin mr-2" size={14} /> : <Upload size={14} className="mr-2" />}
            {saving ? 'Saving…' : 'Choose JSON'}
          </Button>
          {fcm?.configured && (
            <Button
              variant="outline"
              onClick={removeFcm}
              disabled={removing}
              data-testid="fcm-remove-button"
              className="text-red-600 border-red-200 hover:bg-red-50"
            >
              {removing ? <Loader2 className="animate-spin" size={14} /> : <Trash2 size={14} />}
            </Button>
          )}
        </div>

        {/* test sender */}
        {fcm?.configured && (
          <div className="mt-4 rounded-lg border border-gray-200 p-3">
            <Label className="text-xs">Send test push</Label>
            <div className="flex gap-2 mt-1.5">
              <Input
                value={testToken}
                onChange={(e) => setTestToken(e.target.value)}
                placeholder="Paste a device FCM token (e.g. from app debug logs)…"
                className="font-mono text-xs"
                data-testid="fcm-test-token-input"
              />
              <Button
                onClick={sendTest}
                disabled={testing || !testToken.trim()}
                data-testid="fcm-send-test-button"
                style={{ backgroundColor: '#0E7490' }}
                className="text-white"
              >
                {testing ? <Loader2 className="animate-spin mr-1" size={14} /> : <Send size={14} className="mr-1" />}
                Test
              </Button>
            </div>
            <p className="text-[11px] text-gray-500 mt-1.5">
              Get one device token from your mobile app's debug logs, paste it here, and you should
              see a push within a second.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Settings;
